import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBackend } from '../../lib/session'
import { useToast } from '../../components/Toast'
import { errorText } from '../../components/ui'
import { ensureTrailingText, isEditable, upgradePins } from '../../lib/blocks'
import { countWords } from '../../lib/text'
import { cleanAttribution, sanitizeInlineHtml } from '../../lib/sanitize'
import { pruneMarks } from '../../lib/narration'
import { safeFrame } from '../../lib/frame'
import type { Block, Writing } from '../../lib/types'
import { normalizeUnfolds } from './blockOps'
import { normalizeColumns } from '../../lib/columns'
import { BlockHistory, changedEditables, rebase } from './blockHistory'

export type Doc = Pick<
  Writing,
  'title' | 'subtitle' | 'cover_url' | 'cover_frame' | 'blocks' | 'show_times' | 'kind' | 'reflection_date' | 'narration'
>
export type SaveState = 'saved' | 'unsaved' | 'saving' | 'error'

const AUTOSAVE_MS = 700

export function docFrom(w: Writing): Doc {
  return {
    title: w.title,
    subtitle: w.subtitle,
    cover_url: w.cover_url,
    cover_frame: w.cover_url ? safeFrame(w.cover_frame) : null,
    // Old pins become pinned margin notes (lib/blocks.ts upgradePins).
    blocks: normalizeColumns(ensureTrailingText(upgradePins(w.blocks))),
    show_times: w.show_times,
    kind: w.kind,
    reflection_date: w.reflection_date,
    narration: w.narration ?? null,
  }
}

/** Inline HTML is cleaned on the way out: paragraphs, headings, pull quotes and scene lines; a quote's attribution too (plain text). */
function cleanBlocks(blocks: Block[]): Block[] {
  return blocks.map((b) => {
    if (b.type === 'pullquote') {
      const { attribution, ...rest } = b
      const said = cleanAttribution(attribution)
      return { ...rest, html: sanitizeInlineHtml(b.html), ...(said ? { attribution: said } : {}) }
    }
    return isEditable(b) || b.type === 'scene' ? { ...b, html: sanitizeInlineHtml(b.html) } : b
  })
}

/**
 * The composer's document and its autosave: edits land in state at once and
 * are written ~0.7s after the last one (retrying every 5s after a failure).
 * `savedVersion` counts successful saves, for History's auto-snapshots.
 */
export function useComposerDoc(initial: Writing) {
  const backend = useBackend()
  const toast = useToast()
  const [doc, setDoc] = useState<Doc>(() => docFrom(initial))
  const docRef = useRef(doc)
  docRef.current = doc
  const [saveState, setSaveState] = useState<SaveState>('saved')
  const [savedAt, setSavedAt] = useState(() => new Date(initial.updated_at))
  const [savedVersion, setSavedVersion] = useState(0)
  const dirty = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const inflight = useRef<Promise<void> | null>(null)
  const persistRef = useRef<(strict?: boolean) => Promise<void>>(async () => {})
  const mounted = useRef(true)
  /** Retries left once the composer has closed (no toasts then, and not forever). */
  const retriesAfterClose = useRef(3)

  /**
   * Write pending edits. Autosave swallows a failure (it toasts and retries);
   * `strict` callers — History, preview — get the rejection so they stop
   * rather than act on a document that didn't save.
   */
  const persist = useCallback(async (strict = false): Promise<void> => {
    clearTimeout(timer.current)
    // Wait out every save in flight, including ones queued by other callers
    // while we waited, so a strict caller never returns ahead of a save.
    while (inflight.current) await inflight.current.catch(() => {})
    if (!dirty.current) return
    dirty.current = false
    let d = docRef.current
    // Unfolds whose words were edited away become column thoughts; moved words take their unfold along.
    const norm = normalizeUnfolds(d.blocks)
    if (norm.blocks !== d.blocks) {
      d = { ...d, blocks: norm.blocks }
      docRef.current = d
      setDoc((x) => ({ ...x, blocks: normalizeUnfolds(x.blocks).blocks }))
      if (norm.detached) toast(norm.detached === 1 ? 'An unfold lost its words, so it’s a thought in the column now.' : 'Some unfolds lost their words, so they’re thoughts in the column now.')
    }
    const blocks = cleanBlocks(d.blocks)
    const narration = d.narration ? { ...d.narration, marks: pruneMarks(blocks, d.narration.marks ?? {}) } : null
    setSaveState('saving')
    const run = backend
      .updateWriting(initial.id, { ...d, blocks, narration, word_count: countWords(d.title, blocks) })
      .then(() => {
        setSavedAt(new Date())
        setSaveState(dirty.current ? 'unsaved' : 'saved')
        setSavedVersion((v) => v + 1)
      })
      .catch((e: unknown) => {
        dirty.current = true
        if (mounted.current) {
          setSaveState('error')
          toast(errorText(e))
          timer.current = setTimeout(() => void persistRef.current(), 5000)
        } else if (retriesAfterClose.current-- > 0) {
          // The composer is gone: keep trying quietly for a little while.
          timer.current = setTimeout(() => void persistRef.current(), 5000)
        }
        throw e
      })
    inflight.current = run
    try {
      await run
    } catch (e) {
      if (strict) throw e
    } finally {
      inflight.current = null
    }
  }, [backend, initial.id, toast])
  persistRef.current = persist

  /** Any change, through the one autosave path. */
  const apply = useCallback((fn: (d: Doc) => Doc) => {
    setDoc(fn)
    dirty.current = true
    setSaveState('unsaved')
    clearTimeout(timer.current)
    timer.current = setTimeout(() => void persistRef.current(), AUTOSAVE_MS)
  }, [])

  // ── Undo for block operations (blockHistory.ts) ────────────────────────
  // A fresh stack for each composer: it starts empty when a writing loads.
  const history = useRef<BlockHistory>(null as unknown as BlockHistory)
  history.current ??= new BlockHistory()
  /** Text (anything not a block operation) changed since the last block operation, undo or redo. */
  const textSinceOp = useRef(false)
  /** The last thing that happened was one of our undos (so ⇧⌘Z in a paragraph is ours). */
  const lastWasUndo = useRef(false)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const syncHistory = useCallback(() => {
    setCanUndo(history.current.canUndo)
    setCanRedo(history.current.canRedo)
  }, [])

  /** An edit that isn't a block operation: typing, a caption, the title. */
  const update = useCallback(
    (fn: (d: Doc) => Doc) => {
      textSinceOp.current = true
      lastWasUndo.current = false
      apply(fn)
    },
    [apply],
  )

  // Every edit leaves the Columns markers in shape (lib/columns.ts): the same array when they already are.
  const setBlocks = useCallback((fn: (bs: Block[]) => Block[]) => update((d) => ({ ...d, blocks: normalizeColumns(fn(d.blocks)) })), [update])

  /**
   * A block operation — moving, sizing or cropping an image, a gutter,
   * inserting or removing a block: the same as `setBlocks`, and undoable.
   * Inside a gesture (`gesture.begin`) every step joins the one entry.
   */
  /** A gesture (`gesture.begin`) is being held now: block operations are its steps. */
  const holding = useRef(false)

  const blockOp = useCallback(
    (fn: (bs: Block[]) => Block[]) => {
      const textBefore = textSinceOp.current
      // Read now, not when React runs the updater: a gesture's last step can land after it lets go.
      const inGesture = holding.current
      textSinceOp.current = false
      lastWasUndo.current = false
      apply((d) => {
        const blocks = normalizeColumns(fn(d.blocks))
        history.current.record(d.blocks, blocks, textBefore, inGesture)
        return { ...d, blocks }
      })
    },
    [apply],
  )

  /** A drag or resize that writes as it goes: one undo entry from `begin` to `end`; `cancel` puts it back. */
  const gesture = useMemo(
    () => ({
      begin: () => {
        history.current.begin(docRef.current.blocks, textSinceOp.current)
        holding.current = true
        textSinceOp.current = false
        lastWasUndo.current = false
      },
      end: () => {
        holding.current = false
        history.current.end()
      },
      cancel: () => {
        holding.current = false
        const from = history.current.cancel()
        if (from) {
          docRef.current = { ...docRef.current, blocks: from }
          apply((d) => ({ ...d, blocks: from }))
        }
        syncHistory()
      },
    }),
    [apply, syncHistory],
  )

  /**
   * Step the block operations back (or forward) once. Returns the text blocks
   * whose words or kind changed, for the caller to remount, or null when
   * there was nothing to do.
   */
  const step = useCallback(
    (dir: 'undo' | 'redo'): string[] | null => {
      // A gesture nobody is holding any more (its release never came) mustn't block undo.
      if (!holding.current) history.current.close()
      const e = dir === 'undo' ? history.current.undo() : history.current.redo()
      if (!e) return null
      const now = docRef.current.blocks
      const next = normalizeColumns(ensureTrailingText(dir === 'undo' ? rebase(now, e.after, e.before) : rebase(now, e.before, e.after)))
      docRef.current = { ...docRef.current, blocks: next }
      apply((d) => ({ ...d, blocks: next }))
      textSinceOp.current = dir === 'undo' ? e.textBefore : false
      lastWasUndo.current = dir === 'undo'
      syncHistory()
      return changedEditables(now, next)
    },
    [apply, syncHistory],
  )

  // Records land when React runs their updaters: once the document has rendered, close a finished gesture and read the stack.
  useEffect(() => {
    history.current.settle()
    syncHistory()
  }, [doc.blocks, syncHistory])

  const undoState = useCallback(
    () => ({ textSinceOp: textSinceOp.current, lastWasUndo: lastWasUndo.current, canUndo: history.current.canUndo, canRedo: history.current.canRedo }),
    [],
  )

  /** Write pending edits now (History, preview, publish); rejects if they couldn't be saved. */
  const saveNow = useCallback(() => persistRef.current(true), [])

  /** Swap in a writing from elsewhere (a restored version), dropping pending edits. */
  const replace = useCallback((w: Writing) => {
    clearTimeout(timer.current)
    dirty.current = false
    history.current.clear()
    textSinceOp.current = false
    lastWasUndo.current = false
    syncHistory()
    const next = docFrom(w)
    docRef.current = next
    setDoc(next)
    setSaveState('saved')
    setSavedAt(new Date(w.updated_at))
  }, [syncHistory])

  useEffect(() => {
    mounted.current = true
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current || inflight.current) {
        void persistRef.current()
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      mounted.current = false
      if (dirty.current) void persistRef.current()
    }
  }, [])

  return {
    doc,
    docRef,
    update,
    setBlocks,
    blockOp,
    gesture,
    undo: () => step('undo'),
    redo: () => step('redo'),
    undoState,
    canUndo,
    canRedo,
    saveNow,
    replace,
    saveState,
    savedAt,
    savedVersion,
  }
}
